import { ArrowDownUp, ChevronDown, ChevronRight, RefreshCw } from 'lucide-react'
import { useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { ActionMenu, type ActionMenuItem } from '@/design-system/components/ActionMenu/ActionMenu'
import { Button } from '@/design-system/components/Button/Button'
import type { DataTableSelection, DataTableSort } from '@/design-system/components/DataTable/DataTable'
import { IconButton } from '@/design-system/components/IconButton/IconButton'
import { ErrorState, LoadingState } from '@/design-system/components/QueryState/QueryState'
import { mergeClassNames } from '@/design-system/lib/mergeClassNames'
import { ListMobileCards, type ListMobileAction, type ListMobileCard } from './ListMobileCards'
import {
  COLUMN_GAP,
  resolveListLayout,
  useElementWidth,
  type ListLayout,
  type ListLayoutResolution,
} from './useListLayout'

/**
 * Priorité d'une colonne : 1, toujours visible; 2, visible jusqu'à la tablette paysage;
 * 3, ordinateur seulement — repliée dans la ligne dépliable sur tablette.
 */
export type ListColumnPriority = 1 | 2 | 3

export interface ListColumn<TItem> {
  key: string
  header: string
  /** Piste de grille (`minmax(160px,1fr)`), partagée par l'en-tête et les lignes. */
  width: string
  priority: ListColumnPriority
  /** Montants : `end`, chiffres tabulaires. */
  align?: 'start' | 'end'
  sortable?: boolean
  render: (item: TItem) => ReactNode
}

export interface ListRowAction<TItem> {
  /** « Ouvrir » ou « Gérer ». */
  label: string
  onSelect: (item: TItem) => void
}

export interface ListTableProps<TItem> {
  label: string
  columns: readonly ListColumn<TItem>[]
  items: readonly TItem[]
  rowKey: (item: TItem) => string
  /** Nom lisible de la ligne, pour les libellés accessibles des actions. */
  rowName: (item: TItem) => string
  rowAction: ListRowAction<TItem>
  /** Action fréquente en icône 44 px entre l'action principale et ⋯ (Appeler, Enregistrer un paiement). */
  extraActions?: (item: TItem) => ReactNode
  /** Autres actions existantes, dans le menu ⋯. */
  menuItems?: (item: TItem) => ActionMenuItem[]
  rowClassName?: (item: TItem) => string | undefined
  sort?: DataTableSort | undefined
  onSortChange?: (key: string) => void
  pending?: boolean
  pendingLabel?: string
  error?: boolean
  errorLabel?: string
  onRetry?: () => void
  /** Liste vide ou aucun résultat : le module fournit le message et l'action de sortie. */
  empty?: ReactNode
  /** Pied de liste (pagination, plage affichée). */
  footer?: ReactNode
  /** Largeur de la colonne Actions sur ordinateur, en pixels. */
  actionsWidth?: number
  /** Forcer un format (tests, aperçu); par défaut, celui que permet la largeur de la liste. */
  layout?: ListLayout
  /**
   * Cases à cocher (actions groupées existantes). Portée explicite : « tout cocher » ne vise que
   * les lignes affichées. Cocher n'ouvre jamais la fiche.
   */
  selection?: DataTableSelection<TItem>
  /**
   * Résumé compact sur téléphone (format portrait) : la carte entière ouvre la fiche, l'appui long
   * ouvre les actions secondaires. Sans lui, la carte-ligne reprend les colonnes de priorité 1.
   */
  mobileCard?: (item: TItem) => ListMobileCard
  /** Actions de l'appui long (après « Ouvrir »); par défaut, celles du menu ⋯. */
  mobileActions?: (item: TItem) => readonly ListMobileAction[]
}

const SELECTION_WIDTH = 44

const FOLD_BUTTON_WIDTH = 52

const forcedResolution: Record<ListLayout, ListLayoutResolution> = {
  desktop: { layout: 'desktop', maxPriority: 3 },
  tablet: { layout: 'tablet', maxPriority: 2 },
  portrait: { layout: 'portrait', maxPriority: 1 },
}

function visibleColumns<TItem>(
  columns: readonly ListColumn<TItem>[],
  { layout, maxPriority }: ListLayoutResolution,
) {
  if (layout === 'portrait') {
    return { main: [], folded: columns.slice(1).filter((column) => column.priority > 1) }
  }
  return {
    main: columns.filter((column) => column.priority <= maxPriority),
    folded: columns.filter((column) => column.priority > maxPriority),
  }
}

/**
 * Tableau commun des listes RECA : une seule grille partagée entre en-tête et lignes, cellules à
 * deux niveaux, colonne Actions de largeur fixe (« Ouvrir »/« Gérer » puis ⋯). Les colonnes
 * se replient selon leur priorité au lieu de tronquer le texte ou de faire défiler la page.
 */
export function ListTable<TItem>({
  label,
  columns,
  items,
  rowKey,
  rowName,
  rowAction,
  extraActions,
  menuItems,
  rowClassName,
  sort,
  onSortChange,
  pending = false,
  pendingLabel = 'Chargement…',
  error = false,
  errorLabel = 'Cette liste est indisponible pour le moment.',
  onRetry,
  empty,
  footer,
  actionsWidth = 136,
  layout: forcedLayout,
  selection,
  mobileCard,
  mobileActions,
}: ListTableProps<TItem>) {
  const container = useRef<HTMLDivElement>(null)
  const width = useElementWidth(container)
  const resolution = forcedLayout
    ? forcedResolution[forcedLayout]
    : // La case de sélection est une piste de plus : sa largeur et sa gouttière.
      resolveListLayout(width, columns, actionsWidth + (selection ? SELECTION_WIDTH + COLUMN_GAP : 0))
  const { layout } = resolution
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const { main, folded } = visibleColumns(columns, resolution)
  const hasFold = folded.length > 0
  const toggle = (key: string) => {
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const showRows = !pending && !error && items.length > 0
  // Sur tablette, les boutons passent à 44 px : la colonne s'élargit d'autant.
  const actionsColumn = actionsWidth + (layout === 'desktop' ? 0 : 16) + (hasFold ? FOLD_BUTTON_WIDTH : 0)
  const gridStyle = {
    '--table-columns': [
      ...(selection ? [`${String(SELECTION_WIDTH)}px`] : []),
      ...main.map((column) => column.width),
      `${String(actionsColumn)}px`,
    ].join(' '),
  } as CSSProperties

  const actionsFor = (item: TItem, name: string, withFold: boolean) => {
    const key = rowKey(item)
    const isOpen = expanded.has(key)
    return (
      <>
        <Button
          type="button"
          variant="secondary"
          size={layout === 'desktop' ? 'small' : 'medium'}
          aria-label={`${rowAction.label} ${name}`}
          onClick={() => {
            rowAction.onSelect(item)
          }}
        >
          {rowAction.label}
        </Button>
        {extraActions?.(item)}
        <ActionMenu items={menuItems?.(item) ?? []} label={`Plus d’actions pour ${name}`} />
        {withFold ? (
          <IconButton
            icon={isOpen ? ChevronDown : ChevronRight}
            label={isOpen ? `Masquer les détails de ${name}` : `Afficher les détails de ${name}`}
            aria-expanded={isOpen}
            onClick={() => {
              toggle(key)
            }}
          />
        ) : null}
      </>
    )
  }

  const checkbox = (item: TItem, name: string) =>
    selection ? (
      <input
        type="checkbox"
        className="ds-list-table__check"
        aria-label={`Sélectionner ${name}`}
        checked={selection.isSelected(item)}
        onChange={() => {
          selection.onToggle(item)
        }}
      />
    ) : null

  const foldPanel = (item: TItem) => (
    <dl className="ds-list-fold">
      {folded.map((column) => (
        <div key={column.key} className="ds-list-fold__item">
          <dt>{column.header}</dt>
          <dd>{column.render(item)}</dd>
        </div>
      ))}
    </dl>
  )

  const states = (
    <>
      {pending ? (
        <div className="ds-list-skeleton" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="ds-list-skeleton__row">
              <span />
              <span />
              <span />
            </span>
          ))}
        </div>
      ) : null}
      {pending ? <LoadingState>{pendingLabel}</LoadingState> : null}
      {error ? (
        <div className="ds-list-error">
          <ErrorState>{errorLabel}</ErrorState>
          {onRetry ? (
            <Button type="button" variant="secondary" icon={RefreshCw} onClick={onRetry}>
              Réessayer
            </Button>
          ) : null}
        </div>
      ) : null}
      {!pending && !error && items.length === 0 ? empty : null}
    </>
  )

  if (layout === 'portrait' && mobileCard) {
    return (
      <div ref={container} className={`ds-list ds-list--${layout}`}>
        {states}
        {showRows ? (
          <ListMobileCards
            label={label}
            items={items}
            rowKey={rowKey}
            rowName={rowName}
            card={mobileCard}
            openLabel={rowAction.label}
            onOpen={rowAction.onSelect}
            actions={mobileActions ?? ((item) => menuItems?.(item) ?? [])}
            rowClassName={rowClassName}
            leading={selection ? checkbox : undefined}
          />
        ) : null}
        {footer}
      </div>
    )
  }

  if (layout === 'portrait') {
    const [identity, ...rest] = columns
    const summary = rest.filter((column) => column.priority === 1)
    return (
      <div ref={container} className={`ds-list ds-list--${layout}`}>
        {states}
        {showRows && identity ? (
          <div className="ds-list-cards" role="list" aria-label={label}>
            {items.map((item) => {
              const key = rowKey(item)
              const name = rowName(item)
              const isOpen = expanded.has(key)
              return (
                <div
                  key={key}
                  role="listitem"
                  className={mergeClassNames('ds-list-card', rowClassName?.(item))}
                >
                  <div className="ds-list-card__identity">
                    {checkbox(item, name)}
                    {identity.render(item)}
                  </div>
                  {summary.length > 0 ? (
                    <dl className="ds-list-card__summary">
                      {summary.map((column) => (
                        <div key={column.key} className="ds-list-fold__item">
                          <dt>{column.header}</dt>
                          <dd>{column.render(item)}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {isOpen && hasFold ? foldPanel(item) : null}
                  <div className="ds-list-card__actions">
                    {actionsFor(item, name, false)}
                    {hasFold ? (
                      <Button
                        type="button"
                        variant="ghost"
                        className="ds-list-card__fold"
                        aria-expanded={isOpen}
                        aria-label={
                          isOpen ? `Masquer les détails de ${name}` : `Afficher les détails de ${name}`
                        }
                        iconEnd={
                          isOpen ? (
                            <ChevronDown aria-hidden="true" size={16} />
                          ) : (
                            <ChevronRight aria-hidden="true" size={16} />
                          )
                        }
                        onClick={() => {
                          toggle(key)
                        }}
                      >
                        {isOpen ? 'Masquer' : 'Détails'}
                      </Button>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
        {footer}
      </div>
    )
  }

  return (
    <div ref={container} className={`ds-list ds-list--${layout}`}>
      <div className="master-table ds-list-table" role="table" aria-label={label} style={gridStyle}>
        <div className="master-table__head" role="row">
          {selection ? (
            <span role="columnheader" className="ds-list-table__check-cell">
              <input
                type="checkbox"
                className="ds-list-table__check"
                aria-label="Sélectionner toutes les lignes affichées"
                checked={selection.allSelected}
                ref={(input) => {
                  if (input) input.indeterminate = !selection.allSelected && selection.someSelected
                }}
                onChange={selection.onToggleAll}
              />
            </span>
          ) : null}
          {main.map((column) => {
            const active = sort?.key === column.key
            return (
              <span
                key={column.key}
                role="columnheader"
                className={column.align === 'end' ? 'master-table__cell--numeric' : undefined}
                aria-sort={
                  column.sortable
                    ? active
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                    : undefined
                }
              >
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    className={mergeClassNames('master-table__sort', active && 'master-table__sort--active')}
                    onClick={() => {
                      onSortChange(column.key)
                    }}
                  >
                    {column.header}
                    <ArrowDownUp aria-hidden="true" size={12} />
                  </button>
                ) : (
                  column.header
                )}
              </span>
            )
          })}
          <span role="columnheader" className="ds-list-table__actions-head">
            Actions
          </span>
        </div>
        {states}
        {showRows
          ? items.map((item) => {
              const key = rowKey(item)
              const name = rowName(item)
              const isOpen = hasFold && expanded.has(key)
              return (
                <div
                  key={key}
                  role="rowgroup"
                  className={mergeClassNames('ds-list-table__group', rowClassName?.(item))}
                >
                  <div className="master-table__row ds-list-table__row" role="row">
                    {selection ? (
                      <span role="cell" className="ds-list-table__check-cell">
                        {checkbox(item, name)}
                      </span>
                    ) : null}
                    {main.map((column) => (
                      <span
                        key={column.key}
                        role="cell"
                        className={column.align === 'end' ? 'master-table__cell--numeric' : undefined}
                      >
                        {column.render(item)}
                      </span>
                    ))}
                    <span role="cell" className="ds-list-table__actions">
                      {actionsFor(item, name, hasFold)}
                    </span>
                  </div>
                  {isOpen ? foldPanel(item) : null}
                </div>
              )
            })
          : null}
      </div>
      {footer}
    </div>
  )
}
